import { SourceTypes } from '../../../constants';
import type { DataServiceResponseItemAuthor } from '../../types';
import type { LinkedinSourceValue } from '../sources/linkedin/types';
import { parseLinkedinSource } from '../sources/linkedin/utils/parseLinkedinSource';
import type { AuthorAdapter } from '../sources/types';
import { buildProxyURL } from '../utils/buildProxyUrl';
import { getMediaWithProxy } from '../utils/getMediaWithProxy';
import type { DataServiceApiResponse } from '../utils/handleResponseError';

interface LinkedinResponseItemAuthor
  extends Omit<
    DataServiceResponseItemAuthor,
    'postCount' | 'followingCount' | 'isVerifiedProfile'
  > {
  isVerified: boolean;
}

export type LinkedinAuthorResponse =
  DataServiceApiResponse<LinkedinResponseItemAuthor>;

export const linkedinProfileAdapter: AuthorAdapter<LinkedinAuthorResponse> = {
  isApplicable: (source: LinkedinSourceValue) => {
    const isSuccess = 'status' in source && source.status === 'success';
    const isCorrectPlatform = source.platform === SourceTypes.LINKEDIN;
    const isCorrectConnectionType = source.connectionType === 'linkedinSource';

    return (
      !!source.source &&
      isSuccess &&
      isCorrectPlatform &&
      isCorrectConnectionType
    );
  },

  buildUrl: (source: LinkedinSourceValue) => {
    const { source: parsedSource, connectionKey } = parseLinkedinSource(
      source.source!
    );

    return buildProxyURL({
      apiSource: 'linkedin/author',
      params: { [connectionKey]: parsedSource },
      shouldUseWidgetDataService: true
    });
  },

  mapResponse: (response) => {
    const { payload } = response;

    const {
      followersCount,
      isVerified,
      profilePictureUrl,
      username,
      biography,
      name,
      postsCount,
      url
    } = payload;

    return {
      verified: isVerified,
      image: getMediaWithProxy(profilePictureUrl),
      followers: followersCount,
      name,
      username,
      biography,
      postsCount,
      url
    };
  }
};
